import React, { useContext, useEffect, useState } from 'react';
import { Button, Col, Form, Modal, ModalBody, ModalFooter, Row } from 'react-bootstrap';

import {
    faCheckCircle,
    faChevronLeft,
    faChevronRight,
    faExclamationCircle,
    faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import ModalHeader from 'react-bootstrap/ModalHeader';
import LoadingSpinner from '../../../../common/LoadingSpinner';
import axios from 'axios';
import AuthContext from '../../../../authentication/AuthContext';
import { useAlert } from 'react-alert';
import SubmissionAdvertising from './advertising/SubmissionAdvertising';
import OrderContext from '../../OrderContext';
import UserContext from '../../../../user/UserContext';
import { IconWrapper } from '../../../../common/IconWrapper';
import TradingAgreementNotice from './TradingAgreementNotice';
import OrderHeaderMoqProgress from '../moq/OrderHeaderMoqProgress';

// Alternative to props that allows you to define which props to pass to a component.
// Also helps with IntelliJ autocomplete!
export default function OrderSubmission({ onSubmitComplete }) {
    const [showSubmissionModal, setShowSubmissionModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingMessage, setLoadingMessage] = useState('Checking Order');
    const [submissionError, setSubmissionError] = useState(false);
    const [errorResponse, setErrorResponse] = useState();
    const [submissionAvailable, setSubmissionAvailable] = useState(true);
    const [maintenanceMessage, setMaintenanceMessage] = useState('');
    const [paymentTaken, setPaymentTaken] = useState(false);
    const [issuesCount, setIssuesCount] = useState();

    const cas = useContext(AuthContext);
    const { userDetails } = useContext(UserContext);
    const { orderHeader, refreshOrderInfo, basket, setTabKey } = useContext(OrderContext);

    const alert = useAlert();

    useEffect(() => {
        axios
            .get(window.CONFIG['ws.activoc-web-order-api.maintenance.status'], {
                headers: {
                    Authorization: cas,
                    'Content-Type': 'application/json'
                }
            })
            .then(response => {
                setMaintenanceMessage(response.data.maintenanceMessage);
                setSubmissionAvailable(false);
            })
            .catch(error => {
                if (error.response?.status === 404) {
                    setSubmissionAvailable(true);
                } else {
                    alert.error('ERROR: Unable to load submission availability status.');
                }
            });
        // eslint-disable-next-line
    }, [cas]);

    const submitOrder = () => {
        setLoading(true);
        setLoadingMessage('Completing Order');
        axios
            .post(
                window.CONFIG['ws.activoc-web-order-api.orders.confirm'].replace('{id}', orderHeader.id),
                userDetails.currentFranchise.payOnOrder && userDetails.telesales
                    ? { paymentAmountTaken: orderHeader.totalCostIncVat }
                    : {},
                {
                    headers: {
                        Authorization: cas,
                        'Content-Type': 'application/json'
                    }
                }
            )
            .then(() => {
                setLoading(false);
                refreshOrderInfo();
                if (onSubmitComplete) {
                    onSubmitComplete();
                }
                setShowSubmissionModal(false);
            })
            .catch(error => {
                if (error.response) {
                    setLoading(false);
                    setErrorResponse(error.response.data);
                    setSubmissionError(true);
                    refreshOrderInfo();
                } else {
                    alert.error('ERROR: Unable to submit order.');
                }
            });
    };

    useEffect(() => {
        setIssuesCount(
            basket.rejected.length +
                basket.alternatives.length +
                basket.presell.length +
                basket.substitutes.length +
                basket.contentLines.length +
                basket.capped.length
        );
    }, [basket]);

    return (
        <div className={'order-submission'}>
            {!submissionAvailable && <div className={'order-submission-message'}>{maintenanceMessage}</div>}
            <Button
                variant={'primary'}
                className={'btn-action'}
                disabled={!submissionAvailable}
                onClick={() => {
                    setLoading(true);
                    setLoadingMessage('Checking Order');
                    setShowSubmissionModal(true);
                    setSubmissionError(false);
                    axios
                        .post(
                            window.CONFIG['ws.activoc-web-order-api.orders.submit'].replace(
                                '{id}',
                                orderHeader.id
                            ),
                            {},
                            {
                                headers: {
                                    Authorization: cas,
                                    'Content-Type': 'application/json'
                                }
                            }
                        )
                        .then(() => {
                            setLoading(false);
                            refreshOrderInfo();
                        })
                        .catch(error => {
                            if (error.response) {
                                setLoading(false);
                                setErrorResponse(error.response.data);
                                setSubmissionError(true);
                            } else {
                                alert.error('ERROR: Unable to pre-submit order.');
                            }
                        });
                }}
            >
                Submit
                <IconWrapper icon={faChevronRight} />
            </Button>
            <Modal
                onHide={() => setShowSubmissionModal(false)}
                show={showSubmissionModal}
                size={'lg'}
                dialogClassName={'order-submission'}
            >
                {loading ? (
                    <LoadingSpinner message={loadingMessage} />
                ) : (
                    <>
                        <ModalHeader closeButton>
                            <h2>Submit Order</h2>
                        </ModalHeader>
                        <ModalBody>
                            {submissionError ? (
                                <Row className={'order-status-information flex'}>
                                    <div className={'icon'}>
                                        <IconWrapper icon={faInfoCircle} />
                                    </div>
                                    <div className={'text'}>
                                        <span className={'top-row'}>Unexpected error submitting order</span>
                                        <p>{errorResponse.message}</p>
                                        <p>{errorResponse.correlationId}</p>
                                    </div>
                                </Row>
                            ) : (
                                <>
                                    <Row>
                                        <SubmissionAdvertising />
                                    </Row>
                                    {basket.rejected.length === 0 ? (
                                        <Row className={'dialog-header flex'}>
                                            <span>
                                                <IconWrapper icon={faCheckCircle} />
                                            </span>
                                            <div>
                                                Are you sure you want to complete your order?
                                                <TradingAgreementNotice />
                                            </div>
                                        </Row>
                                    ) : (
                                        <Row className={'dialog-header flex'}>
                                            <span>
                                                <IconWrapper icon={faInfoCircle} />
                                            </span>
                                            <div>
                                                <p>
                                                    <span>There {issuesCount < 2 ? 'is' : 'are'} </span>
                                                    <span className={'issues-badge'}>{issuesCount}</span>
                                                    <span>
                                                        {' '}
                                                        issue
                                                        {issuesCount > 1 ? 's' : ''} with your order
                                                    </span>
                                                </p>
                                                <p className={'subtext'}>
                                                    You can still submit it, but you might not receive all of
                                                    your items.
                                                    <TradingAgreementNotice />
                                                </p>
                                            </div>
                                        </Row>
                                    )}
                                    <Row>
                                        {!(
                                            userDetails.currentFranchise.payOnOrder && userDetails.telesales
                                        ) && <Col sm={2} />}
                                        <Col sm={3} className={'order-totals'}>
                                            <p>
                                                <strong>Total lines: </strong>
                                                {orderHeader.totalLines}
                                            </p>
                                            <p>
                                                <strong>Total cases: </strong>
                                                {orderHeader.totalCases}
                                            </p>
                                        </Col>
                                        {!(
                                            userDetails.currentFranchise.payOnOrder && userDetails.telesales
                                        ) && <Col sm={2} />}
                                        <Col sm={3}>
                                            <p>
                                                <strong>Total cost: </strong>
                                            </p>
                                            <p>
                                                <strong className={'order-cost'}>
                                                    £{Math.floor(orderHeader.totalCost)}
                                                    <sup>
                                                        {(orderHeader.totalCost % 1).toFixed(2).substring(1)}
                                                    </sup>
                                                </strong>
                                            </p>
                                        </Col>
                                        {userDetails.currentFranchise.payOnOrder && userDetails.telesales && (
                                            <>
                                                <Col sm={3}>
                                                    <p>
                                                        <strong>VAT: </strong>
                                                    </p>
                                                    <p>
                                                        <strong className={'order-cost'}>
                                                            £{Math.floor(orderHeader.totalVat)}
                                                            <sup>
                                                                {(orderHeader.totalVat % 1)
                                                                    .toFixed(2)
                                                                    .substring(1)}
                                                            </sup>
                                                        </strong>
                                                    </p>
                                                </Col>
                                                <Col sm={3}>
                                                    <p>
                                                        <strong>Total cost incl VAT: </strong>
                                                    </p>
                                                    <p>
                                                        <strong className={'order-cost'}>
                                                            £{Math.floor(orderHeader.totalCostIncVat)}
                                                            <sup>
                                                                {(orderHeader.totalCostIncVat % 1)
                                                                    .toFixed(2)
                                                                    .substring(1)}
                                                            </sup>
                                                        </strong>
                                                    </p>
                                                </Col>
                                            </>
                                        )}
                                        {!(
                                            userDetails.currentFranchise.payOnOrder && userDetails.telesales
                                        ) && <Col sm={2} />}
                                    </Row>
                                    <Row>
                                        <OrderHeaderMoqProgress />
                                    </Row>
                                    {userDetails.telesales && userDetails.currentFranchise.payOnOrder && (
                                        <Row>
                                            <Col sm={7} />
                                            <Col sm={5} className={'payment-taken-container'}>
                                                <Form.Control
                                                    id={'payment-taken'}
                                                    type={'checkbox'}
                                                    onChange={event => setPaymentTaken(event.target.checked)}
                                                    checked={paymentTaken}
                                                />
                                                <Form.Label
                                                    htmlFor={'payment-taken'}
                                                    className={'payment-taken-label'}
                                                >
                                                    Payment £{Math.floor(orderHeader.totalCostIncVat)}
                                                    <sup>
                                                        {(orderHeader.totalCostIncVat % 1)
                                                            .toFixed(2)
                                                            .substring(1)}
                                                    </sup>{' '}
                                                    accepted successfully
                                                </Form.Label>
                                            </Col>
                                        </Row>
                                    )}
                                </>
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <Button
                                id={'order-review'}
                                variant={'secondary'}
                                onClick={() => {
                                    setShowSubmissionModal(false);
                                    setTabKey(issuesCount > 0 ? 'issues' : 'accepted');
                                }}
                            >
                                <IconWrapper icon={faChevronLeft} />
                                Review
                            </Button>
                            {!submissionError && (
                                <div className={'flex submit-button-container'}>
                                    {userDetails.currentFranchise.payOnOrder && !userDetails.telesales && (
                                        <div
                                            id={'credit-control-message'}
                                            className={'flex credit-control-message'}
                                        >
                                            <IconWrapper icon={faExclamationCircle} />
                                            <div>
                                                <span>To complete your order please call </span>
                                                <br />
                                                <span>
                                                    credit control on <strong>01904 232 430</strong>
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                    <Button
                                        variant={'secondary'}
                                        className={'logout'}
                                        onClick={submitOrder}
                                        disabled={
                                            userDetails.currentFranchise.payOnOrder &&
                                            !(userDetails.telesales && paymentTaken)
                                        }
                                    >
                                        Complete Order
                                        <IconWrapper icon={faChevronRight} />
                                    </Button>
                                </div>
                            )}
                        </ModalFooter>
                    </>
                )}
            </Modal>
        </div>
    );
}
